/**
 * Αντιστοιχίσεις προϊόντων πλατφορμών ΜΕ ΤΟ ΟΝΟΜΑ (efood / Wolt / BOX).
 *
 * ΓΙΑΤΙ ξεχωριστό αρχείο και όχι νέα ενότητα στο services/api.ts: το api.ts
 * είναι ~950 γραμμές και το ακουμπούν σχεδόν όλες οι οθόνες — κάθε προσθήκη
 * εκεί γίνεται conflict σε παράλληλη δουλειά. Χρησιμοποιούμε όμως ΤΟ ΙΔΙΟ
 * axios instance (default export του ./api), ώστε να ισχύουν τα interceptors:
 * Bearer token, ανανέωση από x-new-token, 401 → login, 410 → οθόνη μεταφοράς.
 *
 * Συμβόλαιο: docs/platform_name_mapping.md §8 (HTTP API), §9 (frontend) και
 * §12.5/§12.6 (φάση 2α: «ανά επιλογή» και σπάσιμο combo).
 */

import api from './api';

export type PlatformName = 'efood' | 'wolt' | 'box';
export type MappingStatus = 'pending' | 'mapped' | 'ignored' | 'rejected' | 'orphaned';
export type MappingMode = 'off' | 'shadow' | 'on';
export type MappingSizeMode = 'auto' | 'fixed' | 'none';
export type MappingMatchSource = 'exact' | 'history' | 'manual';

/**
 * ΤΙ ΕΙΝΑΙ η γραμμή (spec §12.1).
 *  • 'product'   → ένα συγκεκριμένο προϊόν του καταλόγου (φάση 1),
 *  • 'by_option' → το είδος το ορίζει η ΕΠΙΛΟΓΗ του πελάτη («Χυμός» → «life
 *    πορτοκάλι 0.4 l»): ο δικός της στόχος είναι ΠΡΟΑΙΡΕΤΙΚΟΣ και λειτουργεί ως
 *    εφεδρεία όταν δεν ταιριάξει καμία επιλογή,
 *  • 'ignore'    → δεν είναι προϊόν μας (σχόλια, χρεώσεις).
 * Το 'bundle' είναι δεσμευμένο από τη φάση 1 — η οθόνη δεν το γράφει ποτέ, αλλά
 * ΠΡΕΠΕΙ να το δέχεται γιατί μπορεί να υπάρχει ήδη σε γραμμή της βάσης.
 */
export type MappingTargetKind = 'product' | 'ignore' | 'by_option' | 'bundle';

/**
 * Σταθερό συστατικό μιας γραμμής by_option (το νερό του combo): προϊόν που
 * μπαίνει ΠΑΝΤΑ στο σπάσιμο, χωρίς να υπάρχει σε καμία επιλογή του πελάτη.
 * Εμβέλεια οργανισμού → `org_product_id`, εμβέλεια καταστήματος →
 * `internal_product_id` (spec §12.1). Το `name` το γεμίζει η οθόνη από τον
 * κατάλογο που έχει ήδη φορτώσει — το backend στέλνει μόνο ids και ποσότητα.
 */
export interface MappingFixedComponent {
  org_product_id?: number | null;
  internal_product_id?: number | null;
  quantity: number;
  name?: string | null;
}

/** Ίδια όρια με το PUT του backend (spec §12.5) — τα ελέγχουμε ΚΑΙ εδώ. */
export const MAX_FIXED_COMPONENTS = 10;
export const MAX_COMPONENT_QUANTITY = 20;

/** Το id ενός συστατικού, σε όποια εμβέλεια κι αν γράφτηκε. */
export const fixedComponentId = (c: MappingFixedComponent | null | undefined): number | null => {
  if (!c) return null;
  if (c.internal_product_id != null) return Number(c.internal_product_id);
  if (c.org_product_id != null) return Number(c.org_product_id);
  return null;
};

/**
 * Άμυνα απέναντι στο JSONB: η στήλη `fixed_components` γράφεται από άνθρωπο και
 * μπορεί να έρθει string, null ή σκουπίδια. Ό,τι δεν έχει id πετιέται σιωπηλά —
 * ίδια λογική με τον resolver (backend/utils/platformNameResolver.js).
 */
export const parseFixedComponents = (value: any): MappingFixedComponent[] => {
  let raw = value;
  if (typeof raw === 'string') {
    try { raw = JSON.parse(raw); } catch { return []; }
  }
  if (!Array.isArray(raw)) return [];
  const out: MappingFixedComponent[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const id = fixedComponentId(entry);
    if (id == null) continue;
    const quantity = Number(entry.quantity);
    out.push({
      org_product_id: entry.org_product_id != null ? Number(entry.org_product_id) : null,
      internal_product_id: entry.internal_product_id != null ? Number(entry.internal_product_id) : null,
      quantity: isFinite(quantity) && quantity >= 1 ? Math.min(Math.round(quantity), MAX_COMPONENT_QUANTITY) : 1,
      name: entry.name != null ? String(entry.name) : null,
    });
    if (out.length >= MAX_FIXED_COMPONENTS) break;
  }
  return out;
};

/** Γραμμές παλαιότερες της φάσης 2α δεν έχουν στήλη είδους → 'product'. */
export const rowTargetKind = (row: { target_kind?: MappingTargetKind | string | null } | null | undefined): MappingTargetKind => {
  const kind = row && row.target_kind ? String(row.target_kind) : 'product';
  return (kind === 'by_option' || kind === 'ignore' || kind === 'bundle') ? kind : 'product';
};

/** Υποψήφιο προϊόν όπως το γράφει ο resolver στο candidates JSONB. */
export interface MappingCandidate {
  /** Κεντρικός κατάλογος (εμβέλεια οργανισμού). */
  org_product_id?: number | null;
  /** Τοπικό προϊόν (εμβέλεια καταστήματος). */
  internal_product_id?: number | null;
  name: string;
  vat: number | null;
  score: number;
  kind: 'exact' | 'fuzzy' | 'history'; // history = παλιά χειροκίνητη απόφαση του καταστήματος (seed)
}

/** Ο στόχος μιας αποφασισμένης γραμμής (org ή store εμβέλεια). */
export interface MappingTarget {
  org_product_id?: number | null;
  internal_product_id?: number | null;
  name?: string | null;
  vat?: number | null;
}

export interface NameMappingRow {
  id: number;
  platform_name: PlatformName;
  display_name: string;
  normalized_name: string;
  status: MappingStatus;
  match_source: MappingMatchSource | null;
  confidence: number | null;
  target: MappingTarget | null;
  size_mode: MappingSizeMode;
  org_size_id: number | null;
  fixed_size_norm: string | null;
  candidates: MappingCandidate[] | null;
  vat_risk: boolean;
  is_bundle_hint: boolean;
  seen_count: number;
  last_seen_at: string | null;
  /** Η Postgres επιστρέφει NUMERIC ως string — ο caller κάνει Number(). */
  last_platform_price: number | string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  /** Μόνο στις κεντρικές γραμμές όπως τις βλέπει ΕΝΑ κατάστημα (store API). */
  local_product_id?: number | null;

  // ── Φάση 2α (spec §12) — προαιρετικά: γραμμές/servers πριν τη migration 222
  // δεν τα στέλνουν και η οθόνη πρέπει να δουλεύει ακριβώς όπως πριν.
  /** 'by_option' = το προϊόν το ορίζει η επιλογή του πελάτη. */
  target_kind?: MappingTargetKind;
  /** Σπάσιμο της γραμμής σε μία γραμμή ανά συστατικό (combo). */
  explode_components?: boolean;
  /** Συστατικά που μπαίνουν πάντα στο σπάσιμο (π.χ. το νερό του combo). */
  fixed_components?: MappingFixedComponent[] | null;
  /** Το όνομα μαζεύτηκε από ΤΙΜΗ ΕΠΙΛΟΓΗΣ, όχι από όνομα προϊόντος. */
  seen_as_option?: boolean;
  /** «Χυμός · Smoothies» — κάτω από ποια προϊόντα εμφανίστηκε η επιλογή. */
  option_context?: string | null;
}

export interface MappingCounts {
  pending: number;
  mapped: number;
  ignored: number;
  rejected: number;
  orphaned: number;
}

/** Κάλυψη 7 ημερών ανά κατάστημα × πλατφόρμα (mapped = internal_product_id NOT NULL). */
export interface MappingCoverage {
  store_id: number;
  store_name: string;
  platform_name: PlatformName;
  lines_7d: number | string;
  mapped_7d: number | string;
  pct: number | string;
}

export interface OrgMappingListParams {
  platform?: PlatformName;
  status?: MappingStatus;
  q?: string;
  /** Το backend περιμένει '1' — δες §8. */
  vat_risk?: 1 | 0;
  /** Μόνο ονόματα που ήρθαν από ΕΠΙΛΟΓΕΣ πελατών (spec §12.5, only_options=1). */
  only_options?: 1 | 0;
  limit?: number;
  offset?: number;
}

export interface OrgMappingListResponse {
  rows: NameMappingRow[];
  counts: MappingCounts;
  coverage: MappingCoverage[];
}

/**
 * Το σώμα του PUT — ΜΟΝΟ ό,τι αλλάζει (τα υπόλοιπα πεδία μένουν ως έχουν).
 * Φάση 2α: `status: 'mapped'` με `target_kind: 'by_option'` ΧΩΡΙΣ στόχο είναι
 * έγκυρο (spec §12.5) — η απόφαση «το είδος το λέει η επιλογή» δεν χρειάζεται
 * προϊόν.
 */
export interface MappingUpdateBody {
  org_product_id?: number | null;
  internal_product_id?: number | null;
  status?: MappingStatus;
  size_mode?: MappingSizeMode;
  org_size_id?: number | null;
  fixed_size_norm?: string | null;
  target_kind?: MappingTargetKind;
  explode_components?: boolean;
  fixed_components?: MappingFixedComponent[] | null;
}

export interface SuggestResult {
  harvested: number;
  suggested: number;
  auto_mapped: number;
}

export interface StoreMappingListResponse {
  rows: NameMappingRow[];
  counts: MappingCounts;
  /** Οι κεντρικές γραμμές του οργανισμού, μεταφρασμένες σε ΑΥΤΟ το κατάστημα (read-only). */
  org_rows: NameMappingRow[];
}

export interface StoreMappingSummary {
  pending: number;
  vat_risk: number;
  unmapped_lines_7d: number;
}

/** Φίλτρα λίστας για την εμβέλεια καταστήματος — ίδια σημασιολογία με του οργανισμού. */
export interface StoreMappingListParams {
  status?: MappingStatus;
  q?: string;
  vat_risk?: 1 | 0;
  /** Μόνο ονόματα που ήρθαν από ΕΠΙΛΟΓΕΣ πελατών (spec §12.5). */
  only_options?: 1 | 0;
  limit?: number;
  offset?: number;
}

/**
 * Το ΑΝΩΤΑΤΟ όριο γραμμών ανά κλήση — ίδιος αριθμός με το MAX_LIMIT του backend
 * (routes/storeNameMappings.js). ΓΙΑΤΙ ρητά εδώ: χωρίς `limit` το backend κόβει
 * στις 200 σιωπηλά, οπότε ένα κατάστημα με 850 εκκρεμή ονόματα έδειχνε 200 κάρτες
 * και τα υπόλοιπα 650 ήταν ΑΠΡΟΣΙΤΑ από την οθόνη, ενώ το πλήθος στο chip έλεγε 850.
 */
export const NAME_MAPPINGS_MAX_LIMIT = 1000;

/**
 * Η ισχύουσα λειτουργία για ΕΝΑ κατάστημα: `mode` είναι αυτό που εφαρμόζεται
 * (COALESCE store → org → 'off'), `store_override` η δική του ρύθμιση όταν υπάρχει
 * (αυτή υπερισχύει και «κρύβει» ένα rollback του ομίλου) και `org_mode` του ομίλου.
 */
export interface StoreMappingModeInfo {
  mode: MappingMode;
  store_override: MappingMode | null;
  org_mode: MappingMode | null;
}

/** Κατάστημα με ΔΙΚΗ ΤΟΥ ρύθμιση, όπως τη γυρίζει το mode endpoint του οργανισμού. */
export interface OrgStoreModeOverride {
  store_id: number;
  store_name: string;
  mode: MappingMode;
}

/**
 * Η λειτουργία του ομίλου μαζί με τα καταστήματα που την παρακάμπτουν.
 * ΓΙΑΤΙ τα overrides: ο resolver διαβάζει COALESCE(store, org, 'off'), άρα ένα
 * rollback σε 'off' από εδώ ΔΕΝ σβήνει όποιο κατάστημα έχει δική του ρύθμιση.
 */
export interface OrgMappingModeInfo {
  mode: MappingMode;
  store_overrides?: OrgStoreModeOverride[];
}

/**
 * Το ελληνικό μήνυμα του backend (403 κεντρικού καταλόγου, 409 «τρέχει ήδη» /
 * «λάθος server») πριν πέσουμε σε γενικό κείμενο της οθόνης.
 */
export const mappingErrorText = (e: any): string => (
  (e && e.response && e.response.data && (e.response.data.error || e.response.data.message)) || ''
);

/**
 * 409 = δεν είναι σφάλμα του χρήστη: ή τρέχει ήδη πέρασμα για την ίδια εμβέλεια,
 * ή το κατάστημα δεν ανήκει σε αυτόν τον server. Θέλει μόνιμο μήνυμα, όχι toast
 * 2,5 δευτερολέπτων με γενικό «Αποτυχία».
 */
export const isMappingConflict = (e: any): boolean => (
  !!e && !!e.response && e.response.status === 409
);

/** Εμβέλεια οργανισμού — ο κεντρικός πίνακας στο OrgDashboard. */
export const orgPlatformMappingsAPI = {
  list: async (orgId: number, params?: OrgMappingListParams): Promise<OrgMappingListResponse> => {
    const response = await api.get(`/organizations/${orgId}/platform-mappings`, { params });
    return response.data;
  },

  /** Harvest + προτάσεις + αυτόματη εφαρμογή ΤΩΡΑ (ο scheduler τρέχει ούτως ή άλλως ανά 5′). */
  suggest: async (orgId: number): Promise<SuggestResult> => {
    const response = await api.post(`/organizations/${orgId}/platform-mappings/suggest`);
    return response.data;
  },

  /** Όλες οι εκκρεμείς με ΕΝΑ ακριβές υποψήφιο και χωρίς ρίσκο ΦΠΑ. */
  acceptExact: async (orgId: number): Promise<{ accepted: number }> => {
    const response = await api.post(`/organizations/${orgId}/platform-mappings/accept-exact`);
    return response.data;
  },

  update: async (orgId: number, id: number, body: MappingUpdateBody): Promise<{ row: NameMappingRow } | NameMappingRow> => {
    const response = await api.put(`/organizations/${orgId}/platform-mappings/${id}`, body);
    return response.data;
  },

  /** Επιστρέφει και τα καταστήματα με δική τους ρύθμιση (store_overrides). */
  getMode: async (orgId: number): Promise<OrgMappingModeInfo> => {
    const response = await api.get(`/organizations/${orgId}/platform-mappings/mode`);
    return response.data;
  },

  setMode: async (orgId: number, mode: MappingMode): Promise<OrgMappingModeInfo> => {
    const response = await api.put(`/organizations/${orgId}/platform-mappings/mode`, { mode });
    return response.data;
  },
};

/**
 * Εμβέλεια καταστήματος — ίδιο πρόθεμα με το productMappings.js ('/product-mappings'),
 * ώστε να μην αλλάξει ο τρόπος που εξουσιοδοτείται το κατάστημα.
 */
export const storeNameMappingsAPI = {
  /**
   * Χωρίς `params.limit` το backend κόβει στις 200 — η οθόνη ΠΡΕΠΕΙ να στέλνει
   * όριο και να το λέει στον χρήστη όταν το πιάσει (δες NAME_MAPPINGS_MAX_LIMIT).
   */
  list: async (
    storeId: number,
    platform: PlatformName,
    params?: StoreMappingListParams
  ): Promise<StoreMappingListResponse> => {
    const response = await api.get(`/product-mappings/${storeId}/name-mappings/${platform}`, { params });
    return response.data;
  },

  suggest: async (storeId: number, platform: PlatformName): Promise<SuggestResult> => {
    const response = await api.post(`/product-mappings/${storeId}/name-mappings/${platform}/suggest`);
    return response.data;
  },

  acceptExact: async (storeId: number, platform: PlatformName): Promise<{ accepted: number }> => {
    const response = await api.post(`/product-mappings/${storeId}/name-mappings/${platform}/accept-exact`);
    return response.data;
  },

  update: async (
    storeId: number,
    platform: PlatformName,
    id: number,
    body: MappingUpdateBody
  ): Promise<{ row: NameMappingRow } | NameMappingRow> => {
    const response = await api.put(`/product-mappings/${storeId}/name-mappings/${platform}/${id}`, body);
    return response.data;
  },

  /** Για το badge στο PlatformIntegration («τόσα εκκρεμούν / τόσα με ρίσκο ΦΠΑ»). */
  summary: async (storeId: number): Promise<StoreMappingSummary> => {
    const response = await api.get(`/product-mappings/${storeId}/name-mappings/summary`);
    return response.data;
  },

  /** Ισχύουσα λειτουργία + αν το κατάστημα έχει δική του ρύθμιση. */
  getMode: async (storeId: number): Promise<StoreMappingModeInfo> => {
    const response = await api.get(`/product-mappings/${storeId}/name-mappings/mode`);
    return response.data;
  },

  /**
   * `mode = null` ΣΒΗΝΕΙ την τοπική ρύθμιση, δηλαδή το κατάστημα ξαναγυρίζει να
   * ακολουθεί τον όμιλο. Χωρίς αυτό ένα rollback του ομίλου σε 'off' δεν έπιανε
   * ποτέ τα καταστήματα με override (COALESCE store → org).
   */
  setMode: async (storeId: number, mode: MappingMode | null): Promise<StoreMappingModeInfo> => {
    const response = await api.put(`/product-mappings/${storeId}/name-mappings/mode`, { mode });
    return response.data;
  },
};

export default orgPlatformMappingsAPI;
